import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Loading, ToastHost } from './components/common';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ModelProvider } from './context/ModelContext';
import { PromptProvider } from './context/PromptContext';
import { RuntimeSettingsProvider } from './context/RuntimeSettingsContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { BatchPage } from './pages/BatchPage';
import { ChatPage } from './pages/ChatPage';
import { ComparePage } from './pages/ComparePage';
import { LoginPage } from './pages/LoginPage';
import { SearchPage } from './pages/SearchPage';
import { SettingsPage } from './pages/SettingsPage';

export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <ThemeSync />
          <Gate />
          <Toasts />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

/**
 * Applies the theme saved on the user's record once we know who they are.
 *
 * A separate component because ThemeProvider sits *outside* AuthProvider — the login
 * screen needs a theme before anyone is signed in — so the theme context cannot read
 * the user directly. This bridges the two without reordering the providers.
 *
 * Keyed on the user id, not the theme value: re-applying whenever the value changed
 * would fight the user's own toggle, snapping it back to the last saved value before
 * the POST had landed.
 */
function ThemeSync() {
  const { user } = useAuth();
  const { applyRemote } = useTheme();
  const userId = user?.id;
  const savedTheme = user?.theme;

  useEffect(() => {
    if (userId && savedTheme) applyRemote(savedTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return null;
}

/**
 * Nothing renders until we know who is signed in.
 *
 * The model and prompt providers are deliberately *inside* the signed-in branch:
 * both fetch on mount, and mounting them before authentication would fire a burst
 * of 401s behind the login form — noise in the log that looks like a fault and is
 * not one.
 */
function Gate() {
  const { checking, isSignedIn } = useAuth();

  if (checking) return <Loading large label="Checking your session…" />;
  if (!isSignedIn) return <LoginPage />;

  return (
    <RuntimeSettingsProvider>
      <ModelProvider>
        <PromptProvider>
          <Routes>
            <Route path="/" element={<ChatPage />} />
            <Route path="/c/:conversationId" element={<ChatPage />} />
            <Route path="/batch" element={<BatchPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/settings" element={<Navigate to="/settings/prompts" replace />} />
            <Route path="/settings/:tab" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PromptProvider>
      </ModelProvider>
    </RuntimeSettingsProvider>
  );
}

function Toasts() {
  const { toasts, dismiss } = useToast();
  return <ToastHost toasts={toasts} onDismiss={dismiss} />;
}
